<template>
	<button
		v-if="canQuickBuy"
		type="button"
		:class="[
			'absolute bottom-0 left-0 right-0 w-full py-2 px-4 text-sm font-medium bg-gray-200 hover:bg-gray-300 transition-colors duration-200 opacity-0 group-hover:opacity-100 z-10',
			isLoading ? 'cursor-not-allowed' : 'cursor-pointer'
		]"
		:disabled="isLoading"
		@click.stop.prevent="onQuickBuy"
	>
		<div v-if="isLoading" class="flex items-center justify-center gap-2">
			<div
				class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent"
				role="status"
			></div>
			<span>{{ t("Adding") }}...</span>
		</div>
		<span v-else>{{ t("Quick Buy") }}</span>
	</button>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { storeToRefs } from "pinia";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ProductItem,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useProductQuickBuy } from "~/composables/useProductQuickBuy";
import { useCatalogAddToCart } from "~/composables/useCatalogAddToCart";

interface Props {
	product: Partial<FilteredProductItem> | ProductItem;
	variants?: FilteredProductItem[];
	allParametrics?: ParametricListValue[];
	showVariants?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	"open-modal": [];
}>();

const { t } = useI18n();
const cartStore = useCartStore();
const { updating } = storeToRefs(cartStore);
const { add } = useCatalogAddToCart();

const isLoading = ref(false);

const { canQuickBuy } = useProductQuickBuy({
	product: props.product,
	variants: props.variants,
	allParametrics: props.allParametrics,
	showVariants: props.showVariants
});

const onQuickBuy = async () => {
	if (!props.product || props.product.IsBuyable === false || isLoading.value) {
		return;
	}

	if (!props.variants || props.variants.length === 0) {
		isLoading.value = true;
		try {
			await add(props.product as ProductItem);
		} catch (error) {
			console.error("Error adding to cart:", error);
		}
		return;
	}

	if (props.variants.length === 1) {
		isLoading.value = true;
		try {
			await add(props.variants[0] as ProductItem);
		} catch (error) {
			console.error("Error adding to cart:", error);
		}
		return;
	}

	if (props.variants.length > 1) {
		emit("open-modal");
	}
};

watch(updating, (newValue) => {
	if (!newValue && isLoading.value) {
		isLoading.value = false;
	}
});
</script>
